import { useState, useEffect, useRef } from "react";
import { db, functions } from "../firebaseConfig";
import { httpsCallable } from "firebase/functions";
import { doc, collection, onSnapshot, updateDoc } from "firebase/firestore";

import { useAuth } from "../AuthContext";
import Header from "../components/HeaderV2";
import common from "../style/screens/CommonV2.module.css";
import style from "../style/screens/AdminV2.module.css";
import ErrMsg from "../ErrMsg";
import { BlockInput, SelectorInput } from "../components/Inputs";
import { PartnerType, usePartner } from "../PartnerContext";

interface admin {
    timestamp: number;
    email: string;
    name: string;
    partnerId: string;
    type: string;
    uid: string;
}

const EMAIL_PATTERN = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/;
type EmailCheckStatus = "idle" | "checking" | "available" | "unavailable" | "stale";

const Admin = () => {
    const { userType } = useAuth();

    return (
        <>
            <Header />
            <div className={common["container"]}>
                <div className={common["wrapper"]}>
                    <h4 className={common["text--title"]}>계정 관리</h4>
                    <p className={common["text--copy"]}>
                        내 계정 정보를 관리하고, 관리자 권한을 추가하거나 수정할 수 있습니다.
                    </p>

                    <MyAccount />

                    {userType === "SUPER_ADMIN" && <AdminTable />}

                    {/* <GenerateAPIToken /> */}
                </div>
            </div>
        </>
    );
};

const typeLabel = (type?: string | null) => {
    switch (type) {
        case "SUPER_ADMIN":
            return "최고 관리자";
        case "GROUP_ADMIN":
            return "파트너사 최고 관리자";
        case "ADMIN":
            return "일반 관리자";
        default:
            return "관리자";
    }
};

const partnerLabel = (partnerId?: string | null) => (partnerId ? partnerId : "인네임");

const validatePartnerSelection = (type: string, partnerId: string, partners: PartnerType[]) => {
    if (type === "SUPER_ADMIN" && partnerId) return "최고 관리자는 파트너사를 선택할 수 없습니다.";
    if (type === "GROUP_ADMIN" && !partnerId) return "파트너사를 선택해주세요.";
    if (partnerId && !partners.some((p) => p.id === partnerId)) return ErrMsg["auth/partnerId-not-found"];
    return "";
};

const partnerEmptyLabel = (type: string) => {
    if (!type) return "권한 먼저 선택";
    if (type === "GROUP_ADMIN") return "파트너사 선택";
    return "인네임";
};

const PartnerSelector = ({
    type,
    partnerId,
    setPartnerId,
    error,
}: {
    type: string;
    partnerId: string;
    setPartnerId: (value: string) => void;
    error?: string;
}) => {
    const { partner } = usePartner();
    const disabled = !type || type === "SUPER_ADMIN";

    return (
        <SelectorInput
            placeholder={type === "GROUP_ADMIN" ? "파트너사 선택" : "파트너사"}
            value={type === "SUPER_ADMIN" ? "" : partnerId}
            setValue={setPartnerId}
            error={error}
            emptyLabel={partnerEmptyLabel(type)}
            disabled={disabled}
        >
            {partner.map((item) => (
                <option key={item.id} value={item.id}>
                    {item.name} ({item.id})
                </option>
            ))}
        </SelectorInput>
    );
};

const MyAccount = () => {
    const { user, userType } = useAuth();
    const [admin, setAdmin] = useState<admin | null>(null);
    const [changeName, setChangeName] = useState<boolean>(false);
    const [changePassword, setChangePassword] = useState<boolean>(false);

    useEffect(() => {
        if (!user?.uid) return;

        const unsubscribe = onSnapshot(doc(db, "admin", user.uid), (snapshot) => {
            setAdmin(snapshot.data() as admin);
        });

        return () => unsubscribe();
    }, [user?.uid]);

    return (
        <section className={style["account-card"]}>
            <div className={style["account-main"]}>
                <div className={style["avatar"]}>{(admin?.name || user?.email || "A").slice(0, 1).toUpperCase()}</div>
                <div className={style["account-copy"]}>
                    <p className={style["section-kicker"]}>내 계정</p>
                    <h5>{admin?.name || "이름 없음"}</h5>
                    <p>{user?.email || admin?.email || "-"}</p>
                </div>
            </div>
            <div className={style["account-meta"]}>
                <InfoItem label="권한" value={typeLabel(admin?.type || userType)} />
                <InfoItem label="파트너사" value={partnerLabel(admin?.partnerId)} />
            </div>
            <div className={style["account-actions"]}>
                <button className={style["button--secondary"]} onClick={() => setChangeName(true)}>
                    이름 변경
                </button>
                <button className={style["button--primary"]} onClick={() => setChangePassword(true)}>
                    비밀번호 변경
                </button>
            </div>

            {changeName && (
                <ChangeName currentName={admin?.name || ""} close={() => setChangeName(false)} />
            )}
            {changePassword && <ChangePassword close={() => setChangePassword(false)} />}
        </section>
    );
};

const InfoItem = ({ label, value }: { label: string; value: string }) => (
    <div className={style["info-item"]}>
        <span>{label}</span>
        <strong>{value}</strong>
    </div>
);

const AdminTable = () => {
    const { user } = useAuth();

    const [search, setSearch] = useState<string>("");
    const [roleFilter, setRoleFilter] = useState<string>("ALL");

    const [createAdmin, setCreateAdmin] = useState<boolean>(false);
    const [editAdmin, setEditAdmin] = useState<boolean>(false);
    const [target, setTarget] = useState<object>({});
    const [admins, setAdmins] = useState<any[]>([]);

    useEffect(() => {
        const unsubscribe = onSnapshot(collection(db, "admin"), (snapshot) => {
            const adminLists = snapshot.docs.map((doc) => doc.data()).sort((a, b) => b.timestamp - a.timestamp);
            setAdmins(adminLists);
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        document.body.style.overflow = createAdmin || editAdmin ? "hidden" : "auto";
    }, [createAdmin, editAdmin]);

    const filteredAdmins = admins.filter((admin) => {
        const keyword = search.toLowerCase();
        const matchesKeyword =
            search === "" ||
            admin.partnerId?.toLowerCase().includes(keyword) ||
            admin.name.toLowerCase().includes(keyword) ||
            admin.email.toLowerCase().includes(keyword);
        const matchesRole = roleFilter === "ALL" || admin.type === roleFilter;
        return matchesKeyword && matchesRole;
    });

    return (
        <>
            {admins && ( // 관리자가 없는 경우는 없음
                <section className={style["admin-panel"]}>
                    <div className={style["panel-header"]}>
                        <div>
                            <p className={style["section-kicker"]}>관리자 목록</p>
                            <h5 className={style["panel-title"]}>등록된 관리자</h5>
                            <p className={style["panel-copy"]}>총 {admins.length}개 계정 중 {filteredAdmins.length}개 표시</p>
                        </div>
                        <button className={style["button--primary"]} onClick={() => setCreateAdmin(true)}>
                            관리자 추가
                        </button>
                    </div>
                    <div className={style["toolbar"]}>
                        <input
                            id="search-admin"
                            className={common["input--kiosk-filter"]}
                            type="text"
                            placeholder="이름, 이메일 또는 파트너사 아이디로 검색"
                            onChange={(e) => {
                                setSearch(e.target.value);
                            }}
                        />
                        <div className={style["segment"]}>
                            <button
                                className={roleFilter === "ALL" ? style["segment--active"] : ""}
                                onClick={() => setRoleFilter("ALL")}
                            >
                                전체
                            </button>
                            <button
                                className={roleFilter === "SUPER_ADMIN" ? style["segment--active"] : ""}
                                onClick={() => setRoleFilter("SUPER_ADMIN")}
                            >
                                최고
                            </button>
                            <button
                                className={roleFilter === "GROUP_ADMIN" ? style["segment--active"] : ""}
                                onClick={() => setRoleFilter("GROUP_ADMIN")}
                            >
                                파트너
                            </button>
                            <button
                                className={roleFilter === "ADMIN" ? style["segment--active"] : ""}
                                onClick={() => setRoleFilter("ADMIN")}
                            >
                                일반
                            </button>
                        </div>
                    </div>
                    <div className={style["wrapper--admin-list"]}>
                        <div className={[style["container--rows"], style["container--rows-title"]].join(" ")}>
                            <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>이메일</p>
                            <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>이름</p>
                            <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>파트너사</p>
                            <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>권한</p>
                            <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>관리</p>
                        </div>
                        {filteredAdmins.length === 0 && <div className={style["empty-state"]}>검색 결과가 없습니다.</div>}
                        {filteredAdmins.map((admin, index) => (
                                <div
                                    key={index}
                                    className={[
                                        style["unit--admin-list"],
                                        admin.uid === user.uid && style["unit--admin-list--me"],
                                    ].join(" ")}
                                >
                                    <div className={style["container--rows"]}>
                                        <p className={style["text--eyebrow"]}>{admin.email}</p>
                                        <p className={style["text--eyebrow"]}>{admin.name}</p>
                                        <p className={style["text--eyebrow"]}>{partnerLabel(admin.partnerId)}</p>
                                        <p className={style["text--eyebrow"]}>
                                            <span className={style["role-badge"]}>{typeLabel(admin.type)}</span>
                                        </p>
                                        <div className={style["row-actions"]}>
                                            {admin.uid === user.uid ? (
                                                <span className={style["me-badge"]}>나</span>
                                            ) : (
                                                <button
                                                    className={style["button--ghost"]}
                                                onClick={() => {
                                                    setTarget(admin);
                                                    setEditAdmin(true);
                                                }}
                                                >
                                                    관리
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                    </div>
                </section>
            )}

            {createAdmin && <CreateAdmin close={() => setCreateAdmin(false)} />}

            {editAdmin && <EditAdmin admin={target as admin} close={() => setEditAdmin(false)} />}
        </>
    );
};

const ChangeName = ({ currentName, close }: { currentName: string; close: () => void }) => {
    const { user } = useAuth();

    const [name, setName] = useState<string>(currentName);
    const [nameError, setNameError] = useState<string>("");

    useEffect(() => {
        if (name && name.length < 2) {
            setNameError(ErrMsg["auth/name-invalid"]);
        } else {
            setNameError("");
        }
    }, [name]);

    const [loading, setLoading] = useState<boolean>(false);
    const [active, setActive] = useState<boolean>(false);

    useEffect(() => {
        setActive(true);
    }, []);

    const hide = () => {
        setActive(false);
        setTimeout(() => {
            close();
        }, 240);
    };

    const change = () => {
        if (!name) {
            setNameError(ErrMsg["auth/name-empty"]);
            return;
        }

        setLoading(true);

        return updateDoc(doc(db, "admin", user.uid), {
            name: name,
        })
            .then(() => {
                setLoading(false);
                hide();
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);
                alert("관리자 정보 수정 중에 에러가 발생했습니다");
                return 0;
            });
    };

    return (
        <div className={[common["container--overlay"], active && common["container--overlay--active"]].join(" ")}>
            <div className={[common["unit--overlay"], style["modal"], active && common["unit--overlay--active"]].join(" ")}>
                <div className={style["modal-header"]}>
                    <div>
                        <p className={style["section-kicker"]}>내 계정</p>
                        <h4 className={style["modal-title"]}>이름 변경</h4>
                        <p className={style["modal-copy"]}>관리자 목록과 사이드바에 표시되는 이름입니다.</p>
                    </div>
                    <button className={style["icon-button"]} onClick={hide} aria-label="닫기">
                        ×
                    </button>
                </div>
                <div className={common["wrapper--overlay"]}>
                    <div className={style["unit--overlay--inputs"]}>
                        <BlockInput type="text" placeholder="이름" value={name} setValue={setName} error={nameError} />
                    </div>
                </div>
                <div className={[common["wrapper--overlay--footer"], style["modal-footer"]].join(" ")}>
                    <button className={style["button--secondary"]} onClick={hide}>
                        취소
                    </button>
                    <button className={style["button--primary"]} onClick={change}>
                        {loading ? "저장 중" : "저장"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ChangePassword = ({ close }: { close: () => void }) => {
    const { passwordChange } = useAuth();

    const [password, setPassword] = useState<string>("");
    const [passwordError, setPasswordError] = useState<string>("");

    useEffect(() => {
        if (password && password.length < 6) {
            setPasswordError(ErrMsg["auth/password-invalid"]);
        } else {
            setPasswordError("");
        }
    }, [password]);

    const [newPassword, setNewPassword] = useState<string>("");
    const [newPasswordError, setNewPasswordError] = useState<string>("");

    useEffect(() => {
        if (newPassword && newPassword.length < 6) {
            setNewPasswordError(ErrMsg["auth/password-invalid"]);
        } else {
            setNewPasswordError("");
        }
    }, [newPassword]);

    const [confirmPassword, setConfirmPassword] = useState<string>("");
    const [confirmPasswordError, setConfirmPasswordError] = useState<string>("");

    useEffect(() => {
        if (confirmPassword && newPassword !== confirmPassword) {
            setConfirmPasswordError(ErrMsg["auth/password-not-match"]);
        } else {
            setConfirmPasswordError("");
        }
    }, [confirmPassword, newPassword]);

    const [loading, setLoading] = useState<boolean>(false);
    const [active, setActive] = useState<boolean>(false);

    useEffect(() => {
        setActive(true);
    }, []);

    const hide = () => {
        setActive(false);
        setTimeout(() => {
            close();
        }, 240);
    };

    const change = async () => {
        if (!password) {
            setPasswordError(ErrMsg["auth/password-empty"]);
            return;
        } else if (!newPassword) {
            setNewPasswordError(ErrMsg["auth/password-empty"]);
            return;
        } else if (!confirmPassword) {
            setConfirmPasswordError(ErrMsg["auth/password-empty"]);
            return;
        }

        setLoading(true);

        try {
            await passwordChange(password, newPassword);
            setLoading(false);
            setPassword("");
            setNewPassword("");
            setConfirmPassword("");
            hide();
        } catch (err: any) {
            console.error(err);
            setLoading(false);
            switch (err.message) {
                case "auth/wrong-password":
                    setPasswordError(ErrMsg[err.message]);
                    break;
                default:
                    alert("비밀번호 변경 중 에러가 발생했습니다");
                    break;
            }
        }
    };

    return (
        <div className={[common["container--overlay"], active && common["container--overlay--active"]].join(" ")}>
            <div className={[common["unit--overlay"], style["modal"], active && common["unit--overlay--active"]].join(" ")}>
                <div className={style["modal-header"]}>
                    <div>
                        <p className={style["section-kicker"]}>보안 설정</p>
                        <h4 className={style["modal-title"]}>비밀번호 변경</h4>
                        <p className={style["modal-copy"]}>현재 비밀번호 확인 후 새 비밀번호로 변경합니다.</p>
                    </div>
                    <button className={style["icon-button"]} onClick={hide} aria-label="닫기">
                        ×
                    </button>
                </div>
                <div className={common["wrapper--overlay"]}>
                    <div className={style["unit--overlay--inputs"]}>
                        <BlockInput
                            type="password"
                            placeholder="현재 비밀번호"
                            value={password}
                            setValue={setPassword}
                            error={passwordError}
                        />

                        <BlockInput
                            type="password"
                            placeholder="새로운 비밀번호"
                            value={newPassword}
                            setValue={setNewPassword}
                            error={newPasswordError}
                        />

                        <BlockInput
                            type="password"
                            placeholder="새로운 비밀번호 확인"
                            value={confirmPassword}
                            setValue={setConfirmPassword}
                            error={confirmPasswordError}
                        />
                    </div>
                </div>
                <div className={[common["wrapper--overlay--footer"], style["modal-footer"]].join(" ")}>
                    <button className={style["button--secondary"]} onClick={hide}>
                        취소
                    </button>
                    <button className={style["button--primary"]} onClick={change}>
                        {loading ? "저장 중" : "변경"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const CreateAdmin = ({ close }: { close: () => void }) => {
    const init = useRef(true);
    const { partner } = usePartner();

    const [name, setName] = useState<string>("");
    const [nameError, setNameError] = useState<string>("");

    useEffect(() => {
        if (name && name.length < 2) {
            setNameError(ErrMsg["auth/name-invalid"]);
        } else {
            setNameError("");
        }
    }, [name]);

    const [type, setType] = useState<string>("");
    const [typeError, setTypeError] = useState<string>("");

    useEffect(() => {
        if (init.current) {
            init.current = false;
            return;
        }

        if (!type) {
            setTypeError(ErrMsg["auth/type-invalid"]);
        } else {
            setTypeError("");
        }
    }, [type]);

    const [partnerId, setPartnerId] = useState<string>("");
    const [partnerIdError, setPartnerIdError] = useState<string>("");

    useEffect(() => {
        const nextPartnerId = type === "SUPER_ADMIN" ? "" : partnerId;
        if (type === "SUPER_ADMIN" && partnerId) {
            setPartnerId("");
        }
        setPartnerIdError(validatePartnerSelection(type, nextPartnerId, partner));
    }, [type, partnerId, partner]);

    const [email, setEmail] = useState<string>("");
    const [emailError, setEmailError] = useState<string>("");
    const [emailCheckStatus, setEmailCheckStatus] = useState<EmailCheckStatus>("idle");
    const [emailCheckMessage, setEmailCheckMessage] = useState<string>("");
    const [checkedEmail, setCheckedEmail] = useState<string>("");

    useEffect(() => {
        if (!email) {
            setEmailError("");
            setEmailCheckStatus("idle");
            setEmailCheckMessage("");
            setCheckedEmail("");
            return;
        }

        if (!EMAIL_PATTERN.test(email)) {
            setEmailError(ErrMsg["auth/email-invalid"]);
            setEmailCheckStatus("idle");
            setEmailCheckMessage("");
            setCheckedEmail("");
            return;
        }

        setEmailError("");

        if (checkedEmail && email !== checkedEmail) {
            setEmailCheckStatus("stale");
            setEmailCheckMessage("이메일이 변경되었습니다. 중복체크를 다시 진행해주세요.");
        }
    }, [email, checkedEmail]);

    const checkEmail = () => {
        const nextEmail = email.trim();
        if (!nextEmail) {
            setEmailError(ErrMsg["auth/email-empty"]);
            return;
        }
        if (!EMAIL_PATTERN.test(nextEmail)) {
            setEmailError(ErrMsg["auth/email-invalid"]);
            return;
        }

        if (nextEmail !== email) {
            setEmail(nextEmail);
        }

        setEmailError("");
        setEmailCheckStatus("checking");
        setEmailCheckMessage("이메일 중복 확인 중입니다.");

        return httpsCallable(functions, "adminEmailCheck")({ email: nextEmail })
            .then((result) => {
                const data = result.data as {
                    code?: boolean;
                    available?: boolean;
                    message?: string;
                };

                setCheckedEmail(nextEmail);

                if (data.available) {
                    setEmailCheckStatus("available");
                    setEmailCheckMessage("사용 가능한 이메일입니다.");
                } else {
                    setEmailCheckStatus("unavailable");
                    setEmailCheckMessage(ErrMsg[data.message || "auth/email-already-in-use"] || "이미 사용 중인 이메일입니다.");
                }
            })
            .catch((err) => {
                console.error(err.message);
                setCheckedEmail("");
                setEmailCheckStatus("unavailable");
                setEmailCheckMessage("중복 확인에 실패했습니다. 다시 시도해주세요.");
            });
    };

    const [password, setPassword] = useState<string>("");
    const [passwordError, setPasswordError] = useState<string>("");

    useEffect(() => {
        if (password && password.length < 6) {
            setPasswordError(ErrMsg["auth/password-invalid"]);
        } else {
            setPasswordError("");
        }
    }, [password]);

    const [confirm, setConfirm] = useState<string>("");
    const [confirmError, setConfirmError] = useState<string>("");

    useEffect(() => {
        if (confirm && confirm !== password) {
            setConfirmError(ErrMsg["auth/password-not-match"]);
        } else {
            setConfirmError("");
        }
    }, [confirm, password]);

    const [loading, setLoading] = useState<boolean>(false);

    const create = async () => {
        if (nameError || typeError || partnerIdError || emailError || passwordError || confirmError) {
            return;
        }

        const nextPartnerId = type === "SUPER_ADMIN" ? "" : partnerId;
        const nextPartnerIdError = validatePartnerSelection(type, nextPartnerId, partner);
        const nextEmail = email.trim();

        if (!name) {
            setNameError(ErrMsg["auth/name-empty"]);
            return;
        } else if (!type) {
            setTypeError(ErrMsg["auth/type-invalid"]);
            return;
        } else if (nextPartnerIdError) {
            setPartnerIdError(nextPartnerIdError);
            return;
        } else if (!nextEmail) {
            setEmailError(ErrMsg["auth/email-empty"]);
            return;
        } else if (emailCheckStatus === "checking") {
            setEmailCheckMessage("이메일 중복 확인이 끝난 뒤 추가해주세요.");
            return;
        } else if (emailCheckStatus !== "available" || checkedEmail !== nextEmail) {
            setEmailCheckStatus(checkedEmail ? "stale" : "unavailable");
            setEmailCheckMessage(checkedEmail ? "이메일이 변경되었습니다. 중복체크를 다시 진행해주세요." : "관리자 이메일 중복체크를 진행해주세요.");
            return;
        } else if (!password) {
            setPasswordError(ErrMsg["auth/password-empty"]);
            return;
        } else if (!confirm) {
            setConfirmError(ErrMsg["auth/password-empty"]);
            return;
        }

        setLoading(true);

        return httpsCallable(
            functions,
            "adminCreate"
        )({
            name: name,
            type: type,
            partnerId: nextPartnerId,
            email: nextEmail,
            password: password,
            confirm: confirm,
        })
            .then((result) => {
                setLoading(false);

                const data = result.data as {
                    code?: string;
                    message?: string;
                };

                if (data.code) {
                    hide();
                }
            })
            .catch((err) => {
                console.error(err.message);
                setLoading(false);
                switch (err.message) {
                    case "auth/name-invalid":
                    case "auth/name-weak":
                        setNameError(ErrMsg[err.message]);
                        break;
                    case "auth/type-invalid":
                        setTypeError(ErrMsg[err.message]);
                        break;
                    case "auth/partnerId-invalid":
                    case "auth/partnerId-not-found":
                        setPartnerIdError(ErrMsg[err.message]);
                        break;
                    case "auth/email-invalid":
                    case "auth/email-already-in-use":
                        setEmailError(ErrMsg[err.message]);
                        break;
                    case "auth/password-invalid":
                    case "auth/password-weak":
                        setPasswordError(ErrMsg[err.message]);
                        break;
                    case "auth/password-not-match":
                        setConfirmError(ErrMsg[err.message]);
                        break;
                    case "auth/not-super-admin":
                        alert("최고 관리자만 생성이 가능합니다");
                        break;
                }

                return false;
            });
    };

    const [active, setActive] = useState<boolean>(false);

    useEffect(() => {
        setActive(true);
    }, []);

    const hide = () => {
        setActive(false);
        setTimeout(() => {
            close();
        }, 520);
    };

    return (
        <div className={[common["container--overlay"], active && common["container--overlay--active"]].join(" ")}>
            <div className={[common["unit--overlay"], style["modal"], active && common["unit--overlay--active"]].join(" ")}>
                <div className={style["modal-header"]}>
                    <div>
                        <p className={style["section-kicker"]}>관리자 계정</p>
                        <h4 className={style["modal-title"]}>관리자 추가</h4>
                        <p className={style["modal-copy"]}>기본 정보와 권한을 입력해 새 관리자 계정을 생성합니다.</p>
                    </div>
                    <button className={style["icon-button"]} onClick={hide} aria-label="닫기">
                        ×
                    </button>
                </div>
                <div className={common["wrapper--overlay"]}>
                    <div className={style["modal-section"]}>
                        <div className={style["section-head"]}>
                            <h5>기본 정보</h5>
                            <p>로그인과 관리자 목록에 사용하는 정보입니다.</p>
                        </div>
                        <div className={style["unit--overlay--inputs"]}>
                            <BlockInput type="text" placeholder="이름" value={name} setValue={setName} error={nameError} />
                            <div className={style["email-check-row"]}>
                                <BlockInput
                                    type="text"
                                    placeholder="이메일"
                                    value={email}
                                    setValue={setEmail}
                                    error={emailError}
                                />
                                <button
                                    type="button"
                                    className={[style["button--secondary"], style["email-check-button"]].join(" ")}
                                    onClick={checkEmail}
                                    disabled={emailCheckStatus === "checking" || !email || Boolean(emailError)}
                                >
                                    {emailCheckStatus === "checking" ? "확인 중" : "중복체크"}
                                </button>
                            </div>
                            {emailCheckMessage && (
                                <p
                                    className={[
                                        style["field-message"],
                                        emailCheckStatus === "available" && style["field-message--success"],
                                        emailCheckStatus !== "available" && style["field-message--error"],
                                    ].join(" ")}
                                >
                                    {emailCheckMessage}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className={style["modal-section"]}>
                        <div className={style["section-head"]}>
                            <h5>권한 설정</h5>
                            <p>최고 관리자는 인네임 계정으로 생성되고, 파트너사 최고 관리자는 파트너사 선택이 필수입니다.</p>
                        </div>
                        <div className={style["unit--overlay--inputs"]}>
                            <SelectorInput placeholder="구분" value={type} setValue={setType} error={typeError}>
                                <option value="ADMIN">일반 관리자</option>
                                <option value="GROUP_ADMIN">파트너사 최고 관리자</option>
                                <option value="SUPER_ADMIN">최고 관리자</option>
                            </SelectorInput>

                            <PartnerSelector
                                type={type}
                                partnerId={partnerId}
                                setPartnerId={setPartnerId}
                                error={partnerIdError}
                            />
                        </div>
                    </div>

                    <div className={style["modal-section"]}>
                        <div className={style["section-head"]}>
                            <h5>초기 비밀번호</h5>
                            <p>관리자가 처음 로그인할 때 사용할 비밀번호입니다.</p>
                        </div>
                        <div className={style["unit--overlay--inputs"]}>
                            <BlockInput
                                type="password"
                                placeholder="비밀번호"
                                value={password}
                                setValue={setPassword}
                                error={passwordError}
                            />

                            <BlockInput
                                type="password"
                                placeholder="비밀번호 확인"
                                value={confirm}
                                setValue={setConfirm}
                                error={confirmError}
                            />
                        </div>
                    </div>
                </div>
                <div className={[common["wrapper--overlay--footer"], style["modal-footer"]].join(" ")}>
                    <button className={style["button--secondary"]} onClick={hide}>
                        취소
                    </button>
                    <button className={style["button--primary"]} onClick={create}>
                        {loading ? "추가 중" : "추가하기"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const EditAdmin = ({ admin, close }: { admin: admin; close: () => void }) => {
    const init = useRef(true);
    const { partner } = usePartner();

    const [name, setName] = useState<string>(admin.name);
    const [nameError, setNameError] = useState<string>("");

    useEffect(() => {
        if (name && name.length < 2) {
            setNameError(ErrMsg["auth/name-invalid"]);
        } else {
            setNameError("");
        }
    }, [name]);

    const [partnerId, setPartnerId] = useState<string>(admin.partnerId);
    const [partnerIdError, setPartnerIdError] = useState<string>("");

    const [type, setType] = useState<string>(admin.type);
    const [typeError, setTypeError] = useState<string>("");

    useEffect(() => {
        if (init.current) {
            init.current = false;
            return;
        }

        if (!type) {
            setTypeError(ErrMsg["auth/type-invalid"]);
        } else {
            setTypeError("");
        }
    }, [type]);

    useEffect(() => {
        const nextPartnerId = type === "SUPER_ADMIN" ? "" : partnerId;
        if (type === "SUPER_ADMIN" && partnerId) {
            setPartnerId("");
        }
        setPartnerIdError(validatePartnerSelection(type, nextPartnerId, partner));
    }, [type, partnerId, partner]);

    const [loading, setLoading] = useState<boolean>(false);

    const edit = async () => {
        if (nameError || typeError || partnerIdError) return;

        const nextPartnerId = type === "SUPER_ADMIN" ? "" : partnerId;
        const nextPartnerIdError = validatePartnerSelection(type, nextPartnerId, partner);
        if (!name) {
            setNameError(ErrMsg["auth/name-empty"]);
            return;
        } else if (!type) {
            setTypeError(ErrMsg["auth/type-invalid"]);
            return;
        } else if (nextPartnerIdError) {
            setPartnerIdError(nextPartnerIdError);
            return;
        }

        setLoading(true);

        return updateDoc(doc(db, "admin", admin.uid), {
            name: name,
            type: type,
            partnerId: nextPartnerId,
        })
            .then(() => {
                setLoading(false);
                hide();
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);
                alert("관리자 정보 수정 중에 에러가 발생했습니다");
                return 0;
            });
    };

    const [resetPasswordModal, setResetPasswordModal] = useState<boolean>(false);

    const [deleteBtn, setDeleteBtn] = useState<"Delete" | "DeleteFill">("Delete");
    const [loadingDelete, setLoadingDelete] = useState<boolean>(false);

    const deleteAdmin = () => {
        if (deleteBtn === "Delete") {
            setDeleteBtn("DeleteFill");
            return;
        }

        setLoadingDelete(true);

        return httpsCallable(
            functions,
            "adminDelete"
        )({
            uid: admin.uid,
        })
            .then((result) => {
                setLoadingDelete(false);
                setDeleteBtn("Delete");

                const data = result.data as {
                    code?: string;
                    message?: string;
                };

                if (data.code) {
                    hide();
                }
            })
            .catch((err) => {
                console.error(err);
                setLoadingDelete(false);
                setDeleteBtn("Delete");

                switch (err.message) {
                    case "auth/same-user":
                        alert("자신은 삭제할 수 없습니다");
                        break;
                    default:
                        alert("에러가 발생했습니다");
                        break;
                }

                return;
            });
    };

    const [active, setActive] = useState<boolean>(false);

    useEffect(() => {
        setActive(true);
    }, []);

    const hide = () => {
        setActive(false);
        setTimeout(() => {
            close();
        }, 520);
    };

    return (
        <div className={[common["container--overlay"], active && common["container--overlay--active"]].join(" ")}>
            <div className={[common["unit--overlay"], style["modal"], active && common["unit--overlay--active"]].join(" ")}>
                <div className={style["modal-header"]}>
                    <div>
                        <p className={style["section-kicker"]}>관리자 계정</p>
                        <h4 className={style["modal-title"]}>계정 수정</h4>
                        <p className={style["modal-copy"]}>{admin.email}</p>
                    </div>
                    <button className={style["icon-button"]} onClick={hide} aria-label="닫기">
                        ×
                    </button>
                </div>
                <div className={common["wrapper--overlay"]}>
                    <div className={style["modal-section"]}>
                        <div className={style["section-head"]}>
                            <h5>계정 정보</h5>
                            <p>이메일은 로그인 식별자로 사용되며 여기서는 수정하지 않습니다.</p>
                        </div>
                        <div className={style["readonly-field"]}>
                            <span>이메일</span>
                            <strong>{admin.email}</strong>
                        </div>
                        <div className={style["unit--overlay--inputs"]}>
                            <BlockInput type="text" placeholder="이름" value={name} setValue={setName} error={nameError} />
                        </div>
                    </div>

                    <div className={style["modal-section"]}>
                        <div className={style["section-head"]}>
                            <h5>권한 설정</h5>
                            <p>최고 관리자는 인네임 계정으로 저장되고, 파트너사 최고 관리자는 파트너사 선택이 필수입니다.</p>
                        </div>
                        <div className={style["unit--overlay--inputs"]}>
                            <SelectorInput placeholder="구분" value={type} setValue={setType} error={typeError}>
                                <option value="ADMIN">일반 관리자</option>
                                <option value="GROUP_ADMIN">파트너사 최고 관리자</option>
                                <option value="SUPER_ADMIN">최고 관리자</option>
                            </SelectorInput>

                            <PartnerSelector
                                type={type}
                                partnerId={partnerId}
                                setPartnerId={setPartnerId}
                                error={partnerIdError}
                            />
                        </div>
                    </div>

                    <div className={[style["modal-section"], style["danger-zone"]].join(" ")}>
                        <div className={style["section-head"]}>
                            <h5>계정 삭제</h5>
                            <p>삭제 후에는 해당 관리자가 관리자 페이지에 접근할 수 없습니다.</p>
                        </div>
                        <button
                            className={[
                                style["button--danger"],
                                deleteBtn === "DeleteFill" && style["button--danger-confirm"],
                            ].join(" ")}
                            onClick={deleteAdmin}
                        >
                            {loadingDelete ? "삭제 중" : deleteBtn === "Delete" ? "삭제하기" : "삭제 확인하기"}
                        </button>
                    </div>
                </div>
                <div className={[common["wrapper--overlay--footer"], style["modal-footer"]].join(" ")}>
                    <div className={style["modal-footer-left"]}>
                        <button className={style["button--secondary"]} onClick={() => setResetPasswordModal(true)}>
                            비밀번호 초기화
                        </button>
                    </div>
                    <div className={style["modal-footer-right"]}>
                        <button className={style["button--secondary"]} onClick={hide}>
                            취소
                        </button>
                        <button className={style["button--primary"]} onClick={edit}>
                            {loading ? "수정 중" : "수정하기"}
                        </button>
                    </div>
                </div>
            </div>

            {resetPasswordModal && <ResetPasswordModal admin={admin} close={() => setResetPasswordModal(false)} />}
        </div>
    );
};

const ResetPasswordModal = ({ admin, close }: { admin: admin; close: () => void }) => {
    const [password, setPassword] = useState<string>("");
    const [passwordError, setPasswordError] = useState<string>("");
    const [confirm, setConfirm] = useState<string>("");
    const [confirmError, setConfirmError] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [active, setActive] = useState<boolean>(false);

    useEffect(() => {
        setActive(true);
    }, []);

    useEffect(() => {
        setPasswordError(password && password.length < 6 ? ErrMsg["auth/password-invalid"] : "");
    }, [password]);

    useEffect(() => {
        setConfirmError(confirm && confirm !== password ? ErrMsg["auth/password-not-match"] : "");
    }, [confirm, password]);

    const hide = () => {
        setActive(false);
        setTimeout(() => {
            close();
        }, 260);
    };

    const resetPassword = () => {
        if (passwordError || confirmError) return;

        if (!password) {
            setPasswordError(ErrMsg["auth/password-empty"]);
            return;
        } else if (!confirm) {
            setConfirmError(ErrMsg["auth/password-empty"]);
            return;
        }

        setLoading(true);

        return httpsCallable(
            functions,
            "adminResetPassword"
        )({
            uid: admin.uid,
            password,
            confirm,
        })
            .then((result) => {
                setLoading(false);

                const data = result.data as {
                    code?: string;
                    message?: string;
                };

                if (data.code) {
                    alert("비밀번호가 초기화되었습니다.");
                    hide();
                }
            })
            .catch((err) => {
                console.error(err.message);
                setLoading(false);

                switch (err.message) {
                    case "auth/password-invalid":
                    case "auth/password-weak":
                        setPasswordError(ErrMsg[err.message]);
                        break;
                    case "auth/password-not-match":
                        setConfirmError(ErrMsg[err.message]);
                        break;
                    case "auth/same-user":
                        alert("자신의 비밀번호는 내 계정에서 변경해주세요.");
                        break;
                    case "auth/not-super-admin":
                        alert("최고 관리자만 초기화할 수 있습니다.");
                        break;
                    default:
                        alert("비밀번호 초기화 중에 에러가 발생했습니다.");
                        break;
                }

                return false;
            });
    };

    return (
        <div className={[common["container--overlay"], style["nested-overlay"], active && common["container--overlay--active"]].join(" ")}>
            <div className={[common["unit--overlay"], style["reset-modal"], active && common["unit--overlay--active"]].join(" ")}>
                <div className={style["modal-header"]}>
                    <div>
                        <p className={style["section-kicker"]}>보안 설정</p>
                        <h4 className={style["modal-title"]}>비밀번호 초기화</h4>
                        <p className={style["modal-copy"]}>{admin.email}</p>
                    </div>
                    <button className={style["icon-button"]} onClick={hide} aria-label="닫기">
                        ×
                    </button>
                </div>
                <div className={common["wrapper--overlay"]}>
                    <div className={style["modal-section"]}>
                        <div className={style["section-head"]}>
                            <h5>새 비밀번호</h5>
                            <p>대상 관리자가 다음 로그인부터 사용할 비밀번호입니다. 6자 이상으로 입력해주세요.</p>
                        </div>
                        <div className={style["unit--overlay--inputs"]}>
                            <BlockInput
                                type="password"
                                placeholder="새 비밀번호"
                                value={password}
                                setValue={setPassword}
                                error={passwordError}
                            />
                            <BlockInput
                                type="password"
                                placeholder="새 비밀번호 확인"
                                value={confirm}
                                setValue={setConfirm}
                                error={confirmError}
                            />
                        </div>
                    </div>
                </div>
                <div className={[common["wrapper--overlay--footer"], style["modal-footer"]].join(" ")}>
                    <button className={style["button--secondary"]} onClick={hide}>
                        취소
                    </button>
                    <button className={style["button--primary"]} onClick={resetPassword}>
                        {loading ? "초기화 중" : "초기화"}
                    </button>
                </div>
            </div>
        </div>
    );
};

/* const GenerateAPIToken = () => {
    const [tokenId, setTokenId] = useState<string>("");
    const [tokenIdErr, setTokenIdErr] = useState<string>("");

    useEffect(() => {
        if (!tokenId) {
            setTokenIdErr(ErrMsg["token/id-invalid"]);
        } else {
            setTokenIdErr("");
        }
    }, [tokenId]);

    const [loading, setLoading] = useState<boolean>(false);

    const generate = () => {
        if (!tokenId) {
            setTokenIdErr(ErrMsg["token/id-invalid"]);
            return;
        }

        setLoading(true);

        return httpsCallable(
            functions,
            "generateJwtToken"
        )({
            id: tokenId,
        })
            .then(({ data }: { data: any }) => {
                setLoading(false);
                alert(`토큰 발급 완료: ${data.token}`);
                console.log(data);
                return;
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);
                alert("에러가 발생했습니다");
                return;
            });
    };

    const [active, setActive] = useState<boolean>(false);

    useEffect(() => {
        setActive(true);
    }, []);

    const hide = () => {
        setActive(false);
    };

    return (
        <div className={[common["container--overlay"], active && common["container--overlay--active"]].join(" ")}>
            <h4 className={[common["text--title"], common["text--title--overlay"]].join(" ")}>토큰 발급하기</h4>

            <div className={[common["unit--overlay"], active && common["unit--overlay--active"]].join(" ")}>
                <div className={common["wrapper--overlay"]}>
                    <h4 className={common["text--overlay"]}>계정 기본 정보</h4>
                    <div className={style["unit--overlay--inputs"]}>
                        <BlockInput
                            type="text"
                            placeholder="아이디"
                            value={tokenId}
                            setValue={setTokenId}
                            error={tokenIdErr}
                        />
                    </div>
                </div>
                <div className={common["wrapper--overlay--footer"]}>
                    <ListButton type="Default" text="취소" onClick={hide} />

                    <ListButton text="수정하기" loading={loading} onClick={generate} />
                </div>
            </div>
        </div>
    );
}; */

export default Admin;
